import React from 'react';
import { Alert, Box, Chip, CircularProgress, Typography } from '@mui/material';

/** خروجی `GET /admin/orders/:id/refund-preview` و `/admin/orders/items/:id/refund-preview` (gatewayRefund.preview سرور) */
export interface RefundPreviewStep {
  key: string;
  title: string;
  detail: string;
  tone: 'info' | 'success' | 'warning' | 'error';
  amount?: number;
}
export interface RefundPreviewData {
  provider: string | null;
  channelText: string;
  mode: string;
  modeLabel: string;
  requestedAmount: number;
  goodsAmount: number;
  grossToBuyer: number;
  surchargeOutstanding: number;
  surchargeReturned: number;
  surchargeKept: number;
  includeSurchargeDefault: boolean;
  includeSurchargeEffective: boolean;
  canToggleSurcharge: boolean;
  sellerDebit: number;
  irreversible: boolean;
  funding: { applies: boolean; deferred: boolean; pool: number; outstanding: number; shortfall: number };
  warnings: string[];
  steps: RefundPreviewStep[];
  maxRefundable?: number;
  alreadyRefunded?: number;
  alreadySettled?: boolean;
}

const fa = (n: number) => Math.round(Number(n) || 0).toLocaleString('fa-IR');
const faNum = (n: number) => String(n).replace(/[0-9]/g, (d) => '۰۱۲۳۴۵۶۷۸۹'[Number(d)]);
const TONE_BG: Record<string, string> = { info: 'rgba(25,118,210,0.10)', success: 'rgba(46,125,50,0.12)', warning: 'rgba(237,108,2,0.14)', error: 'rgba(211,47,47,0.14)' };
const TONE_FG: Record<string, string> = { info: '#1565c0', success: '#2e7d32', warning: '#b45309', error: '#c62828' };

/**
 * «دقیقاً چه اتفاقی می‌افتد؟» — پیش‌نمایش سرور قبل از تأیید ادمین: مبلغ به خریدار، کسر از فروشنده،
 * سرنوشت کارمزد اقساطی، تأمین وجه، وضعیت سفارش و اطلاع‌رسانی — همان ترتیبی که اجرا می‌شود.
 */
const RefundPreviewSteps: React.FC<{ preview: RefundPreviewData | null; loading?: boolean; error?: string | null; compact?: boolean }> = ({ preview, loading, error, compact }) => {
  if (loading && !preview) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 1.5, border: '1px dashed', borderColor: 'divider', borderRadius: 1, mb: 1.5 }}>
        <CircularProgress size={14} />
        <Typography variant="caption" color="text.secondary">در حال محاسبهٔ پیش‌نمایش استرداد…</Typography>
      </Box>
    );
  }
  if (error && !preview) return <Alert severity="warning" sx={{ mb: 1.5, fontSize: 12 }}>پیش‌نمایش استرداد در دسترس نیست: {error}</Alert>;
  if (!preview) return null;
  return (
    <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1.5, p: 1.5, mb: 1.5, opacity: loading ? 0.7 : 1, transition: 'opacity .15s' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, flexWrap: 'wrap' }}>
        <Typography variant="subtitle2" fontWeight={800}>دقیقاً چه اتفاقی می‌افتد؟</Typography>
        <Chip size="small" variant="outlined" label={preview.modeLabel} />
        {preview.irreversible && <Chip size="small" color="error" variant="outlined" label="برگشت‌ناپذیر" />}
      </Box>
      <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap', mb: 1 }}>
        <Chip size="small" label={`به خریدار: ${fa(preview.grossToBuyer)} تومان`} color="primary" variant="outlined" />
        <Chip size="small" label={`از سهم فروشنده: ${fa(preview.sellerDebit)} تومان`} variant="outlined" />
        {preview.surchargeOutstanding > 0 && (
          preview.surchargeKept > 0
            ? <Chip size="small" color="warning" variant="outlined" label={`کارمزد ${fa(preview.surchargeKept)} تومان نزد پلتفرم می‌ماند`} />
            : preview.surchargeReturned > 0
              ? <Chip size="small" variant="outlined" label={`شامل کارمزد ${fa(preview.surchargeReturned)} تومان`} />
              : <Chip size="small" variant="outlined" label={`کارمزد ${fa(preview.surchargeOutstanding)} تومان دست نمی‌خورد`} />
        )}
        {preview.funding?.applies && preview.funding.deferred && <Chip size="small" color="error" variant="outlined" label={`کسری تأمین وجه فروشنده ${fa(preview.funding.shortfall)} تومان`} />}
      </Box>
      {!compact && preview.steps.map((st, i) => (
        <Box key={`${st.key}-${i}`} sx={{ display: 'flex', gap: 1, py: 0.6, borderTop: i ? '1px dashed' : 'none', borderColor: 'divider' }}>
          <Box sx={{ width: 20, height: 20, borderRadius: '50%', flexShrink: 0, mt: '1px', fontSize: 11, fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: TONE_BG[st.tone] || TONE_BG.info, color: TONE_FG[st.tone] || TONE_FG.info }}>
            {faNum(i + 1)}
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="body2" fontWeight={700} sx={{ color: st.tone === 'error' ? 'error.main' : st.tone === 'warning' ? 'warning.dark' : 'text.primary', fontSize: 13 }}>
              {st.title}{st.amount ? ` — ${fa(st.amount)} تومان` : ''}
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.9, display: 'block' }}>{st.detail}</Typography>
          </Box>
        </Box>
      ))}
      {preview.warnings?.length > 0 && (
        <Box sx={{ mt: 1 }}>
          {preview.warnings.map((w, i) => <Typography key={i} variant="caption" sx={{ display: 'block', color: 'warning.dark', lineHeight: 1.9 }}>⚠️ {w}</Typography>)}
        </Box>
      )}
    </Box>
  );
};

export default RefundPreviewSteps;
